<template>
<div class="risk">
    <div class="header">
        <div id="title-box">
                <a href="https://www.2020mom.org/">
                    <img class="title-box pcmmh" src="/static/Policy-Center-HORIZ-logo-RGB-full.svg">
                </a>
        </div>
        <div id="socialLinks">
            <a href="https://www.youtube.com/@PolicyCenterMMH"><img class="socialLinks" src="/static/youtube.svg" /></a>
            <a href="https://twitter.com/PolicyCenterMMH"><img class="socialLinks" src="/static/X_logo_2023_original.svg" /></a>
            <a href="https://github.com/2020Mom/mmh-risk-factors-2025"><img class="socialLinks" src="/static/github.svg" /></a>
            <a href="https://www.linkedin.com/company/pcmmh/"><img class="socialLinks" src="/static/linkedin.svg" /></a>
        </div>
        <div>
            <button id="explore" @click="explore">
                <img class="explore" src="/static/list.svg" />
            </button>
        </div>
    </div>
    <div id="docs" v-show="panelOpen">
        <div id="docContent">
            <div id="overview" class="overview" :style="{display: activeTab == 'ov' ? 'block' : 'none'}">
                <h3 style="padding-top:0vw;">Overview:<br>Maternal Mental Health Risk Factors</h3><br>
                <p>Risk Factors Scores for Maternal Mental Health are derived by an algorithm incorporating:
                19 socio-demographic factors that are shown to be associated with a greater risk of maternal mental health disorders, and
                3 factors which are direct measures of need.<br><br>
                US regions with compounded maternal mental health risk factors may be in greater need of high-quality, person-centered maternal mental health support.</p>
                <br>
            </div>
            <div id="rfscores" class="rfscores" :style="{display: activeTab == 'rs' ? 'block' : 'none'}">
                <h3>Risk Factors Score</h3><br>
                <p>
                    The maternal health risk factors are divided into three tiers, using a scale which allocates:
                    up to (3) points per Tier I qualifier,
                    up to (2) points per Tier II qualifier, and
                    up to (1) point per Tier III qualifier.
                </p><br>
                <p>
                    Additionally, there are several Direct Measures of Need, which are allocated up to (3) points per qualifier.
                    The maximum possible point total is 45.
                </p>
            </div>
            <div id="cprovs" class="cprovs" :style="{display: activeTab == 'cp' ? 'block' : 'none'}">
                <h3>PMH Certified Providers</h3>
                <br>
                <p>A bubble diagram shows the concentration of maternal mental health providers including, maternal mental health certified providers, prescribers trained to treat maternal mental health disorders and Community Based Organizations offering mental health services to maternal populations.</p><br>
            </div>
            <div id="pratio" class="pratio" :style="{display: activeTab == 'pr' ? 'block' : 'none'}">
                <h3 style="padding-top:0vw;">Provider Ratio</h3><br>
                <p>
                    The Provider Ratio is measured as the ratio of providers in a given county per one thousand (1k) estimated annual births in that county.
                </p>
            </div>
            <div id="psgap" class="psgap" :style="{display: activeTab == 'sg' ? 'block' : 'none'}">
                <h3 style="padding-top:0vw;">Provider Shortage Gap</h3><br>
                <p>
                    The Provider Shortage Gap is an estimate of the number of PMH-Certified providers required in order to meet the service level needs of a given county.  
                </p><br>
                <p>
                    The target level Provider Ratio is 5.0 providers per 1,000 annual births.  Therefore, a county with a provider ratio of 4.0 is estimated to have a 20% shortage of providers, requiring an increase of 25% from the current level in order to meet the local service level need.
                </p>
            </div>
        </div>
        <div class="docButton">
            <ul>
                <li v-for="detail in details.details" :key="detail.name" >
                    <button :id="detail.id" @click="toggleTab(detail.id)">{{ detail.name }}</button>
                </li>
            </ul>
        </div>
    </div>
    <div class="introduction">
        <h2>2025 U.S. Maternal Mental Health Risk and Resources Maps</h2>
        <div class="textCols">
            <p>Click on a county to reveal the risk factors score and the Provider coverage detail.</p>
            <h3>Mothers and Families in Need find immediate support</h3>
            <h3>U.S. Maternal Mental Health Hotline</h3>
            <h3>Text or Call 1-833-943-5746</h3>
        </div>
    </div>
    <div class="feature">
        <div class="map">
            <div class="choropleth" ref="mapBox">
                <svg id="choropleth"></svg>
                <div id="mapTooltip" class="map-tooltip" :style="{visibility: hovered ? 'visible' : 'hidden', left: tipPos.left+'px', top: tipPos.top+'px'}">
                    <template v-if="hovered">
                        <div class="tt-title">{{ hovered.name }} {{ hovered.stabrv }}</div>
                        <div class="tt-grid">
                            <span>Risk Factors Score</span><span>{{ Number(hovered.rfs) }}</span>
                            <span>PMH Providers</span><span>{{ Number(hovered.providers) }}</span>
                            <span>Est. Annual Births</span><span>{{ Number(hovered.births).toLocaleString('en-US') }}</span>
                            <span>Provider Ratio</span><span>{{ Number(hovered.providers/hovered.births*1000).toFixed(2) }}</span>
                            <span>Provider Shortage Gap</span><span>{{ Number(hovered.gap).toLocaleString('en-US') }}</span>
                        </div>
                    </template>
                </div>
            </div>
        </div>
        <div class="controls">
            <div class="dials">
                <div class="radio">
                    <div class="input1">
                        <input id="input1" class=".checkbox" type="checkbox" v-model="showProviders" /><label> Maternal Mental Health Providers</label>
                    </div>
                    <div class="input2">
                        <input id="input2" class=".checkbox" type="checkbox" v-model="showGap" /><label> Provider Shortage Gap</label>
                    </div>
                </div>
                <div class="selectAgg">
                    <button id="clear" class="button" type="button" @click="clearList">CLEAR</button>
                    <button id="reset" class="button" type="button" @click="reset">RESET</button>
                </div>
            </div>
        </div>
        <div class="hoverData">
            <div class="aggregate">
                <div class="aggregate-row">
                    <span>Risk Factors Score</span><span id="u1">{{ totals ? totals.rfs : '' }}</span>
                </div>
                <div class="aggregate-row">
                    <span>PMH Providers</span><span id="v1">{{ totals ? totals.providers : '' }}</span>
                </div>
                <div class="aggregate-row">
                    <span>Est. Annual Births</span><span id="x1">{{ totals ? totals.births : '' }}</span>
                </div>
                <div class="aggregate-row">
                    <span>Provider Ratio</span><span id="y1">{{ totals ? totals.ratio : '' }}</span>
                </div>
                <div class="aggregate-row">
                    <span>Provider Shortage Gap</span><span id="z1">{{ totals ? totals.gap : '' }}</span>
                </div>
            </div>
            <div class="list">
                <p id="selectionPrompt">Selected Counties:</p>
                <div id="county-state">
                    <li class="counties" v-for="item in selectedNames" :key="item">
                        {{ item }}
                    </li>
                </div>
            </div>
        </div>
    </div>
    </div>
</template>

<script setup>
import { ref, shallowRef, reactive, computed, watch, onMounted } from 'vue';
import * as d3 from 'd3';
import * as tjsClient from "topojson-client";
// production or preview map, chosen by the build mode in vite.config.js
import usAlbers_states from '@map';
import details from './modules/details.mjs';
import layoutLabels from './modules/labelLayout.mjs';

// STATE
// everything the page shows is derived from these; the map handlers below only write here
const panelOpen = ref(false)        // help panel
const activeTab = ref('ov')         // id from details.mjs, or null when no text is shown
const overlay = ref('providers')    // bubble layer: 'providers', 'gap' or null
const zoomK = ref(1)                // current zoom scale
const selected = shallowRef([])     // clicked county features, in click order
const hovered = shallowRef(null)    // properties of the county under the pointer
const tipPos = reactive({left: 0, top: 0})

const showProviders = computed({
    get: () => overlay.value == 'providers',
    set: (on) => { overlay.value = on ? 'providers' : (overlay.value == 'providers' ? null : overlay.value) }
})
const showGap = computed({
    get: () => overlay.value == 'gap',
    set: (on) => { overlay.value = on ? 'gap' : (overlay.value == 'gap' ? null : overlay.value) }
})

const selectedNames = computed(() => selected.value.map((d) => d.properties.name+", "+d.properties.stabrv))

const totals = computed(() => {
    const props = selected.value.map((d) => d.properties)
    if (props.length == 0) return null
    const providers = d3.sum(props, (p) => Number(p.providers))
    const births = d3.sum(props, (p) => Number(p.births))
    return {
        rfs: Math.round(d3.sum(props, (p) => Number(p.rfs))/props.length),
        providers: providers,
        births: births.toLocaleString('en-US'),
        ratio: (providers*1000/births).toFixed(2),
        gap: d3.sum(props, (p) => Number(p.gap)).toLocaleString('en-US')
    }
})

function explore() {
    panelOpen.value = !panelOpen.value
}

function toggleTab(id) {
    activeTab.value = activeTab.value == id ? null : id
}

function clearList() {
    selected.value = []
}

// MAP DATA
const width = +975
const height = +610
const stroke = "white" // stroke color for borders
const strokeLinecap = "round" // stroke line cap for borders
const strokeLinejoin = "round" // stroke line join for borders

const counties = tjsClient.feature(usAlbers_states, usAlbers_states.objects.counties)
const states = tjsClient.feature(usAlbers_states, usAlbers_states.objects.states)
const statemesh = tjsClient.mesh(usAlbers_states, usAlbers_states.objects.states, (a, b) => a !== b)
const path = d3.geoPath();

// risk score is binned into equal steps up to the highest county score
const levels = 5
const maxScore = d3.max(counties.features, (d) => Number(d.properties.rfs))
const scoreStep = maxScore/levels
const scoreSteps = d3.range(1, levels+1).map((i) => i*scoreStep)
const plumColors =
    d3.scaleLinear()
        .domain([0.2,0.6,0.8])
        .range(["#ebe2ed","#8a5391","#631a6c"])
        .interpolate(d3.interpolateRgb)
const stepColor = (score) => plumColors(score/maxScore)

function scoreColor(score) {
    if (score == 0) return "#181818"
    for (const step of scoreSteps) {
        if (score <= step) return stepColor(step)
    }
}

// MAP
const mapBox = ref(null)
let svg, g, zoom
let touchTooltipFips = null

function moveTooltip(event) {
    if (!mapBox.value) return
    const point = (event.touches && event.touches.length) ? event.touches[0] : event
    const rect = mapBox.value.getBoundingClientRect()
    tipPos.left = point.clientX - rect.left + 14
    tipPos.top = point.clientY - rect.top + 14
}

function showTooltip(event, d) {
    hovered.value = d.properties
    moveTooltip(event)
}

function hideTooltip() {
    hovered.value = null
    touchTooltipFips = null
}

function handleTouchTooltip(event, d) {
    event.stopPropagation()
    if (touchTooltipFips === d.id) {
        hideTooltip()
    } else {
        showTooltip(event, d)
        touchTooltipFips = d.id
    }
}

function bubblesFor(d) {
    return g.selectAll(".circles").selectAll("circle").filter((c) => c.id == d.id)
}

function hoverin(event, d) {
    event.stopPropagation()
    showTooltip(event, d)
    // fade in a bold border, about 2.5px on screen at any zoom
    g.select("#hovered")
        .interrupt()
        .attr("d", path(d))
        .attr("stroke-width", 0)
        .transition().duration(150)
        .attr("stroke-width", 2.5/zoomK.value)
    bubblesFor(d)
        .attr("stroke", "white")
        .attr("stroke-width", 0.3)
}

function hoverout(event, d) {
    event.stopPropagation()
    hideTooltip()
    g.select("#hovered")
        .interrupt()
        .transition().duration(150)
        .attr("stroke-width", 0)
    bubblesFor(d)
        .attr("stroke", "black")
        .attr("stroke-width", null)
}

function clicked(event, d) {
    event.stopPropagation()
    if (selected.value.includes(d)) {
        selected.value = selected.value.filter((s) => s !== d)
    } else {
        selected.value = [...selected.value, d]
    }
    // frame the whole selection, not just the county clicked
    const group = selected.value
    const [[x0, y0], [x1, y1]] = path.bounds(group.length ? {type: "FeatureCollection", features: group} : d)
    svg.transition().duration(750).call(
        zoom.transform,
        d3.zoomIdentity
            .translate(width / 2, height / 2)
            .scale(Math.min(10, 0.9 / Math.max((x1 - x0) / width, (y1 - y0) / height)))
            .translate(-(x0 + x1) / 2, -(y0 + y1) / 2),
        d3.pointer(event, svg.node())
    )
}

function reset() {
    svg.transition().duration(750).call(
        zoom.transform,
        d3.zoomIdentity,
        d3.zoomTransform(svg.node()).invert([width / 2, height / 2])
    )
    clearList()
}

function zoomed(event) {
    const {transform} = event
    g.attr("transform", transform)
    g.attr("stroke-width", Math.round((1 / transform.k)/0.1)/10)
    zoomK.value = transform.k
    // bubbles shrink a little as the map zooms in, up to 10x
    if (transform.k <= 10) {
        const scale = 2-(transform.k-1)*(.59/9)
        g.select("#providers").selectAll("circle")
            .attr("r", (d) => Math.sqrt(d.properties.providers)*scale)
        g.select("#gap").selectAll("circle")
            .attr("r", (d) => Math.sqrt(d.properties.gap)*scale)
    }
}

// the layers below are redrawn from state, never changed by the handlers directly
function drawSelection() {
    // borders sit in their own layer so neighboring counties can't cover them
    g.select("#selected")
        .selectAll("path")
        .data(selected.value, (d) => d.id)
        .join("path")
            .attr("fill", "none")
            .attr("stroke", "black")
            .attr("stroke-width", 0.5)
            .attr("stroke-linejoin", strokeLinejoin)
            .attr("d", path)
}

function drawOverlay() {
    g.select("#providers").attr("visibility", overlay.value == 'providers' ? "visible" : "hidden")
    g.select("#gap").attr("visibility", overlay.value == 'gap' ? "visible" : "hidden")
}

function drawLabelVisibility() {
    const countyLabels = g.select(".labels")
    const stateLabels = g.select(".labels2")
    const k = zoomK.value
    if (k > 6) {
        countyLabels.style("visibility", "visible").style("fill-opacity", k/12+.125)
        stateLabels.style("visibility", "visible").style("fill-opacity", (1 - k/12)*2)
    } else if (k > 2) {
        countyLabels.style("visibility", "hidden")
        stateLabels.style("visibility", "visible").style("fill-opacity", k/6)
    } else {
        countyLabels.style("visibility", "hidden")
        stateLabels.style("visibility", "hidden")
    }
}

watch(selected, drawSelection)
watch(overlay, drawOverlay)
watch(zoomK, drawLabelVisibility)

function makeLegend() {
    const legend = svg.append("g").attr("id", "scales")

    legend.append("rect")
        .attr("id", "riskbg")
        .attr("x", width-70)
        .attr("y", height - height*(0.04*(levels+2.2)))
        .attr("width", 70)
        .attr("height", height*(0.04*(levels+2)))
        .attr("fill", "#242424")
        .attr("opacity", "90%")
        .attr("rx", "8")

    legend.append("text")
        .attr("id","riskLabel")
        .attr("x", width-35)
        .attr("y", height - height*(0.04*(levels+1.5)))
        .attr("fill", "white")
        .attr("text-anchor", "middle")
        .attr("dominant-baseline","middle")
        .attr("font-family", "Montserrat, sans-serif")
        .text('Risk');

    legend.append("g")
        .attr("id","scaleBoxes")
        .selectAll("rect")
        .data(scoreSteps)
        .join("rect")
            .attr("fill", (d) => stepColor(d))
            .attr("x", width-57)
            .attr("y", (d,i) => height-(i*height*.04)-50)
            .attr("height", height*.04)
            .attr("width", height*.04);

    legend.append("g")
        .attr("id","scaleLabels")
        .selectAll("text")
        .data([0, ...scoreSteps])
        .join("text")
            .attr("fill", "white")
            .attr("x", width-24)
            .attr("y", (d,i) => height-15-(i*height*.04))
            .style("font-family", "monospace")
            .attr("font-size", "1.2em")
            .text((d) => Math.round(d).toLocaleString('en-US'));

    legend.append("text")
        .attr("id", "attribution")
        .attr("x", width*0.55)
        .attr("y", height*.995)
        .attr("text-anchor", "left")
        .attr("fill", "black")
        .style("font-family", "monospace")
        .attr("font-size", "0.8em")
        .text('© 2026 Policy Center for Maternal Mental Health')
}

function makeMap() {
    g.append("g")
        .attr("id","counties")
        .selectAll("path")
        .data(counties.features)
        .join("path")
            .attr("id", (d) => d.id)
            .attr("fill", (d) => scoreColor(Number(d.properties.rfs)))
            .attr("d", path)
            .attr("class", "county")
            .on("click", clicked)
            .on("mouseenter", hoverin)
            .on("mousemove", moveTooltip)
            .on("mouseleave", hoverout)
            .on("touchstart", handleTouchTooltip)

    document.addEventListener("touchstart", (event) => {
        if (!event.target.closest(".county")) {
            hideTooltip()
        }
    })

    g.append("path")
        .attr("id","outline")
        .attr("pointer-events", "none")
        .attr("fill", "none")
        .attr("stroke", stroke)
        .attr("stroke-linecap", strokeLinecap)
        .attr("stroke-linejoin", strokeLinejoin)
        .attr("d", path(statemesh))

    g.append("g")
        .attr("id","selected")
        .attr("pointer-events", "none")

    // outline of the county under the pointer, see hoverin/hoverout
    g.append("path")
        .attr("id","hovered")
        .attr("pointer-events", "none")
        .attr("fill", "none")
        .attr("stroke", "black")
        .attr("stroke-linejoin", strokeLinejoin)
        .attr("stroke-width", 0)

    g.append("g")
        .attr("class", "circles")
        .attr("id", "providers")
        .selectAll("circle")
        .data(counties.features.filter(d => d.properties.providers >= 1))
        .join("circle")
            .attr("id", (d) => d.id)
            .attr("cx", (d) => path.centroid(d)[0])
            .attr("cy", (d) => path.centroid(d)[1])
            .attr("r", (d) => Math.sqrt(d.properties.providers)*2)
            .attr("stroke","black")
            .attr("fill", "white")
            .attr("fill-opacity", "50%")
            .style("pointer-events", "none")

    g.append("g")
        .attr("class", "circles")
        .attr("id", "gap")
        .selectAll("circle")
        .data(counties.features.filter(d => d.properties.gap >= 1))
        .join("circle")
            .attr("id", (d) => d.id)
            .attr("cx", (d) => path.centroid(d)[0])
            .attr("cy", (d) => path.centroid(d)[1])
            .attr("r", (d) => Math.sqrt(d.properties.gap)*2)
            .attr("stroke","black")
            .attr("fill", "white")
            .attr("fill-opacity", "50%")
            .style("pointer-events", "none")

    g.append("g")
        .attr("class", "labels2")
        .selectAll("text")
        .data(states.features)
        .join("text")
            .text((d) => d.properties.state)
            .attr("x", (d) => path.centroid(d)[0])
            .attr("y", (d) => path.centroid(d)[1])
            .attr("font-size", ".5em")
            .attr("text-anchor", "middle")
            .attr("dominant-baseline","middle")
            .style("pointer-events", "none")

    const labels = g.append("g")
        .attr("class", "labels")
        .selectAll("text")
        .data(counties.features)
        .join("text")
            .text((d) => d.properties.label)
            .attr("x", (d) => path.centroid(d)[0])
            .attr("y", (d) => path.centroid(d)[1])
            .attr("font-size", "min(0.8em,1.2pt)")
            .attr("text-anchor", "middle")
            .attr("dominant-baseline","middle")
            .style("pointer-events", "none")

    // nudge overlapping county names apart; labels scale with the zoom so this only runs once
    // getBBox is unreliable at this font size, so measure on a canvas at 100px and scale down
    const labelSize = 1.6
    const labelFont = getComputedStyle(labels.node()).fontFamily
    function placeLabels() {
        const ctx = document.createElement("canvas").getContext("2d")
        ctx.font = "100px "+labelFont
        const nodes = []
        labels.each(function(d) {
            const [ax, ay] = path.centroid(d)
            if (!isFinite(ax) || !isFinite(ay)) return
            nodes.push({
                id: d.id,
                ax: ax,
                ay: ay,
                w: ctx.measureText(d.properties.label).width*labelSize/100,
                h: labelSize,
                priority: path.area(d)
            })
        })
        const placed = new Map(layoutLabels(nodes).map((n) => [n.id, n]))
        labels.filter((d) => placed.has(d.id))
            .attr("x", (d) => placed.get(d.id).x)
            .attr("y", (d) => placed.get(d.id).y)
            .style("display", (d) => placed.get(d.id).hidden ? "none" : null)
        // names with no room at full size are set in smaller type
        labels.filter((d) => placed.has(d.id) && placed.get(d.id).scale < 1)
            .attr("font-size", (d) => labelSize*placed.get(d.id).scale+"px")

        // names that had to move off their county get a leader line back to its centroid
        const callouts = nodes.map((n) => ({...n, ...placed.get(n.id)})).filter((n) => n.leader)
        d3.select(labels.node().parentNode)
            .selectAll("line")
            .data(callouts)
            .join((enter) => enter.insert("line", "text"))
                .attr("x1", (n) => n.ax)
                .attr("y1", (n) => n.ay)
                .attr("x2", (n) => n.leader[0])
                .attr("y2", (n) => n.leader[1])
                .attr("stroke", "black")
                .attr("stroke-width", 0.15)
                .attr("stroke-linecap", strokeLinecap)
                .style("pointer-events", "none")
    }
    // measure after the web font loads so the widths are right
    if (document.fonts && document.fonts.load) {
        document.fonts.load("100px "+labelFont).then(placeLabels, placeLabels)
    } else {
        placeLabels()
    }
}

onMounted(() => {
    svg = d3.select("#choropleth")
    g = svg.append("g")

    svg.attr("style", "width: 100%; height: auto; border-radius: 0.5em; outline-style: none;")
        .attr("width", width)
        .attr("height", height)
        .attr("viewBox", [0, 0, width, height])

    zoom = d3.zoom()
        .translateExtent([[width*-0.025, height*-0.025], [width*1.025, height*1.025]])
        .scaleExtent([.9, 12])
        .on("zoom", zoomed)

    makeMap()
    makeLegend()

    svg.call(zoom)
    svg.call(zoom.transform, d3.zoomIdentity)

    drawSelection()
    drawOverlay()
    drawLabelVisibility()
})
</script>

<style>
* {
    max-width: 975px;
    margin:auto;
}
#app {
    top: 0;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    text-align: center;
}

.risk {
    position: sticky;
    top:0;
}

.header {
    position: sticky;
    top:0vw;
    display: grid;
    width: 100%;
    background-color: white;
    grid-template-columns: 2fr 1fr 1.5fr;
    list-style-type: none;
    padding-left: 4vw;
    height: min(9em,12vw);
    padding-left: 3em;
    align-content: center;
    z-index: 10;
    color: #631a6c;
}

#title-box {
    display: grid;
    grid-template-columns: 1fr 1fr;
}

.title-box {
    display:grid;
    grid-template-columns: 1fr 1fr;
    height: min(9em,12vw);
    max-height: max(12vw);
    overflow: hidden;
}

.pcmmh {
    height: min(6vw,6em);
}

.explore {
    text-align: center;
    fill: #1f0970;
    stroke: #1f0970;
    height: min(6em,10vw);
}

.explore:hover {
  background-color: #f2f2f2;
}

button#explore {
    background-color: transparent;
}

#docs, #aboutPC {
    position: fixed;
    display:grid;
    background-color: white;
    opacity: 95%;
    top: min(8em,(10vw+1em));

    z-index: 10;
}

#aboutPC {
    display: grid;
    grid-template-columns: 1fr;
    text-align: left;
    visibility: hidden;
}

#docs {
    display: grid;
    grid-template-columns: 1.5fr 1fr;
    visibility: visible;
    padding-left: 1em;
    padding-right: 1em;
}

.docButton>ul>li>button {
    width: 100%;
    background-color: white;
    font-weight: bold;
    padding: 2vw;
    font-size: max(0.8em,11pt);
}

.docContent {
    max-width: 100%;
    overflow: scroll;
}

#docContent>div>p {
    font-size: max(0.8em,11pt);
}

.docButton {
    font-size: max(0.8em,11pt);
    padding: 2vw;
    width: 70%;
}

.docs {
    align-content: center;
    background-color: white;
    text-align: left;
    width: 100px;

}

.docButton>ul {
    list-style-type: none;
    text-align: left;
}

#docs>ul>button {
    width: 30vw;
}

.introduction>div>p {
    display:block;
    padding-bottom: 1em;
    font-size: min(1em,11pt);
;
}

.textCols {
    padding-top: 1em;
    width: 80%;
}

.textCols>p {
    font-size: 1em;
}

.map {
    outline-style: none;
    outline-color: #242424;
    outline-width: 0.5em;
    margin: 0;
}
.dials {
    align-self: right;
    height: min(8em,120px);
    display: grid;
    grid-template-columns: 60% 20% 20%;
    padding-left: min(1em,4vw);
}

.radio {
    text-align: left;
    font-size: min(2.5vw,1em);
}

#input1, #input2 {
    accent-color: #2f7f34;
}

#clear, #reset {
    margin-top: 0.5em;
    width: min(6em,12vw);
    height: min(2em,6vw);
    background-color: #242424;
    font-size: min(2.5vw,0.8em);
}

.selectAgg {
    display: grid;
}

.hoverData {
    display: grid;
    grid-template-areas:
        "aggregate list";
    padding-left: min(1em,4vw);
    padding-bottom: min(2em,5vw);
    grid-template-columns: 40% 60%;
    height: min(14em,170px);
}

.aggregate {
    align-self: left;
    min-height: 50px;
    grid-area: aggregate;
    display: grid;
    grid-template-columns: auto auto;
    column-gap: 0.75em;
    row-gap: 0.5em;
    font-size: min(2.5vw,1em);
}

.aggregate-row {
    display: contents;
}

.aggregate-row>span:first-child {
    text-align: left;
}

.aggregate-row>span:last-child {
    text-align: right;
    font-weight: 600;
}

.list {
    grid-area: list;
    font-size: min(2.4vw,0.8em);
    align-text: left;
}

#state, #nation, #feature {
    width: 100%;
}

#county-state {
    display: grid;
    grid-template-columns: 1fr;
    align-text:left;
    padding-top:1em;
    overflow-y: scroll;
    max-height: min(6em,14vw);
    list-style-type: none;
}

input:checked {
     border: #232323;
     background-color: #232323;
}

p {
    padding: 0;
    margin: 0.2rem;
}

.tabs {
    display:inline-flex;
}

#selectionPrompt {
    font-style: italic;
    text-align: left;
    padding-left: 20%;
}

#socialLinks {
    display: grid;
    grid-template-columns: auto auto auto auto;
}
#socialLinks>img {
    padding: 1vw;
    fill: #1f0970;
    stroke: #1f0970;
    
}

.socialLinks {
    fill: #1f0970;
    stroke: #1f0970;
    padding: 1vw;
    height: min(4vw,2em);
}

.rfscores, .cprovs, .pratio, .psgap { 
    display: none;
    text-align: justify;
}

.overview {
    display: block;
    text-align: justify;
}

.overview>h3 {
    text-align: left;
}

.choropleth {
    padding: .5em;
    position: relative;
}

.map-tooltip {
    position: absolute;
    visibility: hidden;
    pointer-events: none;
    z-index: 20;
    background-color: #242424;
    opacity: 0.95;
    color: white;
    font-family: Montserrat, sans-serif;
    font-size: 0.75em;
    padding: 0.6em 0.8em;
    border-radius: 8px;
    width: fit-content;
    box-shadow: 0 2px 8px rgba(0,0,0,0.4);
}

.map-tooltip .tt-title {
    font-weight: 700;
    font-size: 1.1em;
    margin-bottom: 0.4em;
    border-bottom: 1px solid rgba(255,255,255,0.3);
    padding-bottom: 0.3em;
    white-space: nowrap;
}

.map-tooltip .tt-grid {
    display: grid;
    grid-template-columns: auto auto;
    column-gap: 0.6em;
    row-gap: 0.1em;
}

.map-tooltip .tt-grid span {
    margin: 0;
    white-space: nowrap;
}

.map-tooltip .tt-grid span:nth-child(odd) {
    text-align: right;
}

.map-tooltip .tt-grid span:nth-child(even) {
    text-align: center;
}

.indicator {
    width: auto;
}

.clickList {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: 1fr 3fr;
    grid-template-areas:
        "selectAgg selectAgg selectAgg"
        "aggregate list list";
}

select {
    font-size: min(1em,4vw);
}

#buttons {
    display: flex;
    width: 80%;
}

.button {
    border-radius: 8px;
    background-color: #242424;
    opacity: 90%;
    border: none;
    color: white;
    text-align: center;
    text-decoration: none;
    height: 5vw;
    background-color: white;
}

    @-webkit-keyframes initLoad{
        0%{opacity: 0;}
        25%{opacity: .25;}
        50%{opacity: .50;}
        75%{opacity: .75;}
        100%{opacity: 1;}
    }

    @keyframes initLoad{
        0%{opacity: 0;}
        25%{opacity: .25;}
        50%{opacity: .50;}
        75%{opacity: .75;}
        100%{opacity: 1;}
    }
</style>
